/** Spoken-command helpers for the live-caption mic (task popup). */

import { appendNeedToCallItem, fetchNeedToCall, createNote } from '../features/notes/services/noteService';
import { notifyNeedToCallChanged } from './suggestedReplies';
import { matchCaptionRules, parseSpokenReminderWhen } from './captionSettings';

const TITLE_RE = /\b(?:(?:keep|make|set)\s+(?:the\s+)?title\s+as|add\s+(?:the\s+)?title(?:\s+as)?)\b[:\-\s]*/gi;
const DESC_RE = /\b(?:(?:keep|make|set)\s+(?:the\s+)?description\s+as|add\s+(?:the\s+)?description(?:\s+as)?)\b[:\-\s]*/gi;
const DELEGATE_RE = /\b(?:delegate(?:d)?(?:\s+it)?|assign(?:ed)?(?:\s+it)?)\s+(?:it\s+)?(?:to|two|too|2)\b[:\-\s]*/gi;
const PUBLISH_RE = /\b(?:we\s+are\s+good(?:\s+with\s+this\s+task)?|(?:please\s+)?publish(?:ed)?(?:\s+it|\s+this\s+task)?(?!\s+(?:to|in|into)\b)|save\s+this\s+task)\b/gi;
const CHANNEL_RE = /\b(?:put(?:\s+it|\s+this)?\s+(?:in|into)|(?:please\s+)?(?:publish(?:ed)?|post|raise)(?:\s+it)?\s+(?:to|in|into))\b[:\-\s]*/gi;
const WORKFLOW_OPEN_RE = /\b(?:create\s+(?:me\s+)?(?:an?\s+)?(?:new\s+)?|open\s+(?:an?\s+)?(?:new\s+)?)(feature|enhancement|bug|defect)s?\b/gi;
const NAME_STOP = /^(?:and|then|also|please|now|the|a|an|thanks|thank)$/i;

export const VOICE_WORKFLOW_TYPE_IDS = {
  feature: 'wf:work-item-type:feature',
  enhancement: 'wf:work-item-type:enhancement',
  bug: 'wf:work-item-type:bug',
  defect: 'wf:work-item-type:defect',
};

const MARKERS = [
  { type: 'openWorkflow', re: WORKFLOW_OPEN_RE },
  { type: 'title', re: TITLE_RE },
  { type: 'description', re: DESC_RE },
  { type: 'delegate', re: DELEGATE_RE },
  { type: 'channel', re: CHANNEL_RE },
  { type: 'publish', re: PUBLISH_RE },
];

const CAPTION_ACTION_MAP = {
  create_task: 'open',
  create_reminder: 'reminder',
  add_call_list: 'needCall',
  call_user: 'callUser',
  create_cart: 'openCart',
  add_cart_item: 'addCartItem',
  create_note: 'createNote',
};

const cleanValue = (raw) =>
  String(raw || '')
    .replace(/^[\s:,\-–—]+/, '')
    .replace(/\b(?:and\s+then|and\s+also|please|thanks|thank\s+you)\b$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const spokenNameFromRemainder = (raw) => {
  const words = cleanValue(raw).split(' ').filter(Boolean);
  const name = [];
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    if (NAME_STOP.test(word)) {
      if (!name.length) continue;
      break;
    }
    name.push(word);
    if (name.length >= 3) break;
  }
  return name.join(' ');
};

const spokenChannelFromRemainder = (raw) =>
  cleanValue(raw)
    .replace(/^(?:the|a|an)\s+/i, '')
    .replace(/\s+(?:chat|group|channel)$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const spokenCartItemFromRemainder = (raw) =>
  cleanValue(raw)
    .replace(/^(?:please\s+)?(?:add\s+)?(?:(?:an?\s+)?(?:item|product)\s+)?(?:to\s+(?:the\s+)?(?:shopping\s+)?cart\s+)?/i, '')
    .replace(/^(?:the|a|an)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeSpoken = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const editDistance = (a, b) => {
  const left = String(a || '');
  const right = String(b || '');
  const rows = left.length + 1;
  const cols = right.length + 1;
  const grid = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 0; i < rows; i += 1) grid[i][0] = i;
  for (let j = 0; j < cols; j += 1) grid[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      grid[i][j] = Math.min(
        grid[i - 1][j] + 1,
        grid[i][j - 1] + 1,
        grid[i - 1][j - 1] + cost,
      );
    }
  }
  return grid[left.length][right.length];
};

const collectMarkers = (text) => {
  const hits = [];
  MARKERS.forEach((marker) => {
    const re = new RegExp(marker.re.source, marker.re.flags);
    let match = re.exec(text);
    while (match) {
      hits.push({
        type: marker.type,
        start: match.index,
        end: match.index + match[0].length,
      });
      match = re.exec(text);
    }
  });
  return hits.sort((a, b) => a.start - b.start);
};

export const parseVoiceTaskUtterance = (utterance) => {
  const text = String(utterance || '').replace(/\s+/g, ' ').trim();
  if (!text) return [];
  const hits = collectMarkers(text);
  if (!hits.length) return [];
  const actions = [];
  hits.forEach((hit, index) => {
    const next = hits[index + 1];
    const remainder = cleanValue(text.slice(hit.end, next ? next.start : text.length));
    if (hit.type === 'open' || hit.type === 'publish') {
      actions.push({ type: hit.type });
      return;
    }
    if (hit.type === 'openWorkflow') {
      const snippet = text.slice(hit.start, hit.end);
      const slug = (snippet.match(/feature|enhancement|bug|defect/i) || [])[0]?.toLowerCase();
      const typeId = VOICE_WORKFLOW_TYPE_IDS[slug];
      if (typeId) actions.push({ type: 'openWorkflow', value: typeId });
      return;
    }
    if (hit.type === 'channel') {
      actions.push({ type: 'channel', value: spokenChannelFromRemainder(remainder) });
      return;
    }
    const value = (hit.type === 'delegate' || hit.type === 'needCall')
      ? spokenNameFromRemainder(remainder)
      : remainder;
    actions.push({ type: hit.type, value });
  });
  return actions;
};

export const personSpokenHaystack = (person) =>
  [
    person?.firstName,
    person?.lastName,
    [person?.firstName, person?.lastName].filter(Boolean).join(' '),
    person?.username,
    person?.email,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const matchPersonBySpokenName = (spoken, people) => {
  const query = normalizeSpoken(spokenNameFromRemainder(spoken));
  if (!query) return null;
  const tokens = query.split(' ').filter(Boolean);
  const spokenFirst = tokens[0] || '';
  let best = null;
  let bestScore = 0;
  (people || []).forEach((person) => {
    const personId = person?.id || person?._id;
    if (!personId) return;
    const hay = personSpokenHaystack(person);
    const firstName = normalizeSpoken(person?.firstName);
    const lastName = normalizeSpoken(person?.lastName);
    if (!hay && !firstName) return;
    let score = 0;
    if (firstName && spokenFirst) {
      if (firstName === spokenFirst || firstName === query) score = 100;
      else if (firstName.startsWith(spokenFirst) && spokenFirst.length >= 3) score = 90;
      else if (spokenFirst.startsWith(firstName) && firstName.length >= 3) score = 88;
      else {
        const distance = editDistance(firstName, spokenFirst);
        if (firstName.length >= 4 && distance <= 1) score = 84;
        else if (firstName.length >= 6 && distance <= 2) score = 76;
      }
    }
    if (firstName && lastName && query === `${firstName} ${lastName}`) score = Math.max(score, 100);
    if (hay === query) score = Math.max(score, 100);
    else if (hay.includes(query) && query.length >= 3) score = Math.max(score, 85);
    else if (query.includes(hay) && hay.length >= 3) score = Math.max(score, 75);
    else {
      const hit = tokens.filter((token) => hay.includes(token)).length;
      if (hit === tokens.length && tokens.length > 0) score = Math.max(score, 70);
      else if (hit > 0) score = Math.max(score, 40 + hit * 8);
    }
    if (score > bestScore) {
      bestScore = score;
      best = { ...person, id: personId };
    }
  });
  return bestScore >= 40 ? best : null;
};

export const matchChannelBySpokenName = (spoken, channels) => {
  const query = normalizeSpoken(spokenChannelFromRemainder(spoken));
  if (!query) return null;
  const tokens = query.split(' ').filter(Boolean);
  let best = null;
  let bestScore = 0;
  (channels || []).forEach((channel) => {
    const id = channel?.id || channel?._id;
    const name = normalizeSpoken(channel?.name);
    if (!id || !name) return;
    let score = 0;
    if (name === query) score = 100;
    else if (name.includes(query) && query.length >= 2) score = 88;
    else if (query.includes(name) && name.length >= 3) score = 80;
    else {
      const hit = tokens.filter((token) => name.includes(token)).length;
      if (hit === tokens.length && tokens.length > 0) score = 72;
      else if (hit > 0) score = 40 + hit * 10;
      const distance = editDistance(name, query);
      if (name.length >= 4 && distance <= 1) score = Math.max(score, 84);
      else if (name.length >= 6 && distance <= 2) score = Math.max(score, 70);
    }
    if (score > bestScore) {
      bestScore = score;
      best = { ...channel, id };
    }
  });
  return bestScore >= 40 ? best : null;
};

export const extractNeedToCallNames = (utterance) =>
  [
    ...parseVoiceTaskUtterance(utterance),
    ...matchCaptionRules(utterance)
      .filter((hit) => hit.action === 'add_call_list')
      .map((hit) => ({ type: 'needCall', value: spokenNameFromRemainder(hit.remainder) })),
  ]
    .filter((action) => action.type === 'needCall' && String(action.value || '').trim())
    .map((action) => String(action.value).trim());

const personLabel = (person, fallback) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()
  || person?.username
  || person?.email
  || String(fallback || '').trim();

const sameCallName = (left, right) =>
  String(left || '').toLowerCase().replace(/\s+/g, ' ').trim()
  === String(right || '').toLowerCase().replace(/\s+/g, ' ').trim();

const isOpenNeedToCall = (item) =>
  !item?.done && item?.status !== 'called' && item?.status !== 'skipped';

export const addNeedToCallFromSpoken = async (spoken, people = []) => {
  const raw = spokenNameFromRemainder(spoken);
  if (!raw) return null;
  const matched = matchPersonBySpokenName(raw, people);
  const name = personLabel(matched, raw);
  if (!name) return null;
  const existing = await fetchNeedToCall();
  const already = (Array.isArray(existing) ? existing : []).some((item) => {
    if (!isOpenNeedToCall(item)) return false;
    if (matched?.id && String(item.targetUserId || '') === String(matched.id)) return true;
    return sameCallName(item.name, name);
  });
  if (already) return { name, skipped: true };
  const items = await appendNeedToCallItem({
    targetUserId: matched?.id || null,
    name,
    phone: String(matched?.mobileNumber || matched?.contactNumber || '').trim(),
    reason: 'From voice',
  });
  notifyNeedToCallChanged(items);
  return { name, skipped: false };
};

export const VOICE_TASK_EVENT = 'workloop:voice-task';
export const CAPTION_CONFIRM_EVENT = 'workloop:caption-confirm';

const CONFIRM_TYPES = new Set([
  'needCall',
  'callUser',
  'reminder',
  'createNote',
  'addCartItem',
]);

const needsCaptionConfirm = (action) => {
  if (!CONFIRM_TYPES.has(action?.type)) return false;
  return Boolean(String(action.value || '').trim());
};

export const captionConfirmCopy = (action) => {
  const value = String(action?.value || '').trim();
  switch (action?.type) {
    case 'needCall':
    case 'callUser':
      return {
        title: 'Add to call reminder?',
        message: value
          ? `Add ${value} to your call reminder?`
          : 'Add this person to your call reminder?',
        confirm: 'Continue',
      };
    case 'reminder':
      return {
        title: 'Create reminder?',
        message: value ? `Create a reminder: ${value}` : 'Create this reminder?',
        confirm: 'Continue',
      };
    case 'createNote':
      return {
        title: 'Save note?',
        message: value ? `Save this as a note: ${value}` : 'Save this note?',
        confirm: 'Continue',
      };
    case 'open':
      return { title: 'Create a task?', message: 'Open the task composer?', confirm: 'Continue' };
    case 'openCart':
      return { title: 'Open shopping cart?', message: 'Create or open a shopping cart?', confirm: 'Continue' };
    case 'addCartItem':
      return {
        title: 'Add to cart?',
        message: value ? `Add “${value}” to the shopping cart?` : 'Add this item to the cart?',
        confirm: 'Continue',
      };
    case 'openWorkflow':
      return { title: 'Create work item?', message: 'Open the work-item composer?', confirm: 'Continue' };
    case 'publish':
      return { title: 'Publish task?', message: 'Save and publish this task?', confirm: 'Continue' };
    default:
      return { title: 'Continue?', message: 'Do you want to continue with this action?', confirm: 'Continue' };
  }
};

let captionQueue = [];
let captionBusy = false;
let confirmResolver = null;

export const resolveCaptionConfirm = (ok) => {
  if (confirmResolver) {
    confirmResolver(Boolean(ok));
    confirmResolver = null;
  }
};

const askCaptionConfirm = (action) => new Promise((resolve) => {
  confirmResolver = resolve;
  window.dispatchEvent(new CustomEvent(CAPTION_CONFIRM_EVENT, { detail: action }));
});

const enqueueCaptionAction = (action) => {
  captionQueue.push(action);
  drainCaptionQueue();
};

const drainCaptionQueue = async () => {
  if (captionBusy) return;
  captionBusy = true;
  while (captionQueue.length) {
    const action = captionQueue.shift();
    if (needsCaptionConfirm(action)) {
      const ok = await askCaptionConfirm(action);
      if (!ok) continue;
    }
    await executeCaptionAction(action);
  }
  captionBusy = false;
};

const openCallReminders = () => {
  window.dispatchEvent(new CustomEvent('workloop:open-notes'));
  window.dispatchEvent(new CustomEvent('workloop:open-call-reminders'));
};

export const emitVoiceTaskCommand = (action, value, extra = {}) => {
  window.dispatchEvent(new CustomEvent(VOICE_TASK_EVENT, {
    detail: { action, value: value || '', ...extra },
  }));
};

let pendingField = null;
let lastEmitKey = '';
let lastEmitAt = 0;

const shouldEmit = (action, value, append) => {
  const key = `${action}:${append ? 'append:' : ''}${value || ''}`;
  const now = Date.now();
  if (key === lastEmitKey && now - lastEmitAt < 6000) return false;
  lastEmitKey = key;
  lastEmitAt = now;
  return true;
};

const fireAction = (action) => {
  enqueueCaptionAction(action);
};

const executeCaptionAction = async (action) => {
  if (action.type === 'needCall' || action.type === 'callUser') {
    pendingField = action.value ? null : 'needCall';
    if (!action.value) {
      emitVoiceTaskCommand('toast', 'Say a name after call');
      return;
    }
    if (!shouldEmit('needCall', action.value, false)) return;
    try {
      const result = await addNeedToCallFromSpoken(action.value);
      if (result?.name && !result.skipped) {
        emitVoiceTaskCommand('toast', `Added ${result.name} to call reminder`);
      }
      if (result?.name) openCallReminders();
    } catch (err) {
      emitVoiceTaskCommand('toast', err?.message || 'Could not add to call reminder');
    }
    return;
  }
  if (action.type === 'open' || action.type === 'publish' || action.type === 'openCart') {
    pendingField = action.type === 'openCart' ? 'addCartItem' : null;
  } else if (action.type === 'title') {
    pendingField = action.value ? null : 'title';
    if (!action.value) return;
  } else if (action.type === 'delegate') {
    pendingField = action.value ? null : 'delegate';
    if (!action.value) return;
  } else if (action.type === 'description') {
    pendingField = 'description';
    if (!action.value) return;
  } else if (action.type === 'openWorkflow') {
    pendingField = null;
    if (!action.value) return;
  } else if (action.type === 'channel') {
    pendingField = action.value ? null : 'channel';
    if (!action.value) return;
  } else if (action.type === 'addCartItem') {
    pendingField = 'addCartItem';
    if (!action.value) return;
  } else if (action.type === 'reminder' || action.type === 'createNote') {
    pendingField = null;
    if (!shouldEmit(action.type, action.value, false)) return;
    if (action.type === 'reminder') {
      createReminderFromSpoken(action.value);
      return;
    }
    createNoteFromSpoken(action.value);
    return;
  }
  if (!shouldEmit(action.type, action.value, action.append)) return;
  emitVoiceTaskCommand(action.type, action.value, { append: Boolean(action.append) });
};

const escapeNoteHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const createNoteFromSpoken = async (spoken) => {
  const body = String(spoken || '').trim();
  const title = (body.split(/[.!?]/)[0] || 'Voice note').trim().slice(0, 80) || 'Voice note';
  try {
    await createNote({
      title,
      contentHtml: `<p>${escapeNoteHtml(body || title)}</p>`,
      attachments: [],
      sensitiveBlocks: [],
    });
    window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
    emitVoiceTaskCommand('toast', `Note saved: ${title}`);
  } catch (err) {
    emitVoiceTaskCommand('toast', err?.response?.data?.error || err?.message || 'Could not save note');
  }
};

const createReminderFromSpoken = async (spoken) => {
  const parsed = parseSpokenReminderWhen(spoken);
  const title = parsed.title || spoken || 'Reminder';
  try {
    const { createReminder } = await import('../components/reminders/reminderService');
    await createReminder({
      title: title.slice(0, 120),
      description: 'From voice',
      enabled: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      recurrence: {
        type: 'once',
        date: parsed.date,
        time: parsed.time,
      },
    });
    emitVoiceTaskCommand('toast', `Reminder: ${title} at ${parsed.time}`);
  } catch (err) {
    emitVoiceTaskCommand('toast', err?.response?.data?.error || err?.message || 'Could not create reminder');
  }
};

const actionsFromCaptionSettings = (text) =>
  matchCaptionRules(text).map((hit) => {
    const type = CAPTION_ACTION_MAP[hit.action];
    if (!type) return null;
    if (type === 'open') {
      const remainder = String(hit.remainder || '').trim();
      const fieldFollows = /\b(?:title|description|delegate|assign|publish)\b/i.test(remainder);
      return { type, value: fieldFollows ? '' : remainder };
    }
    if (type === 'openCart') return { type };
    if (type === 'needCall' || type === 'callUser') {
      return { type, value: spokenNameFromRemainder(hit.remainder) };
    }
    if (type === 'addCartItem') {
      return { type, value: spokenCartItemFromRemainder(hit.remainder) };
    }
    return { type, value: hit.remainder };
  }).filter(Boolean);

const SPOKEN_CALL_RE = /\b(?:please\s+)?(?:call|calling|called|phone|ring)(?:\s+(?:to|up))?[\s:,\-–—]+/i;

export const voiceCallActionsFromSpoken = (utterance) => {
  const text = String(utterance || '').replace(/\s+/g, ' ').trim();
  const match = text.match(SPOKEN_CALL_RE);
  if (!match) return [];
  const value = spokenNameFromRemainder(text.slice((match.index || 0) + match[0].length));
  if (!value) return [];
  if (/^(?:it|this|that|me|us|back|later|now|again|list|user)$/i.test(value)) return [];
  return [{ type: 'needCall', value }];
};

const dedupeVoiceActions = (actions) => {
  const seen = new Set();
  const out = [];
  (actions || []).forEach((action) => {
    const type = action.type === 'callUser' ? 'needCall' : action.type;
    const key = `${type}:${String(action.value || '').trim().toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(type === action.type ? action : { ...action, type });
  });
  return out;
};

export const applyVoiceTaskFromCaption = (spoken) => {
  const text = String(spoken || '').replace(/\s+/g, ' ').trim();
  if (!text) return;
  const hits = collectMarkers(text);
  const fromSettings = actionsFromCaptionSettings(text);
  let actions = dedupeVoiceActions([
    ...voiceCallActionsFromSpoken(text),
    ...fromSettings,
    ...parseVoiceTaskUtterance(text),
  ]);
  if (!actions.length && pendingField) {
    actions = [{
      type: pendingField,
      value: (pendingField === 'delegate' || pendingField === 'needCall')
        ? spokenNameFromRemainder(text)
        : pendingField === 'channel'
          ? spokenChannelFromRemainder(text)
          : text,
      append: pendingField === 'description',
    }];
  } else if (pendingField === 'description' && hits.length && hits[0].type !== 'description') {
    const prefix = cleanValue(text.slice(0, hits[0].start));
    if (prefix) {
      actions = [{ type: 'description', value: prefix, append: true }, ...actions];
    }
  }
  if (!actions.length) return;

  const instantTypes = new Set(['needCall', 'callUser', 'reminder', 'createNote', 'openCart', 'addCartItem']);
  const instant = actions.filter((action) => instantTypes.has(action.type));
  instant.forEach(fireAction);

  const hasOpen = actions.some((action) => action.type === 'open');
  const workflowOpens = actions.filter((action) => action.type === 'openWorkflow');
  const rest = actions.filter((action) => (
    action.type !== 'open'
    && action.type !== 'openWorkflow'
    && !instantTypes.has(action.type)
  ));
  const dialogOpen = Boolean(window.__workloopVoiceTaskOpen);
  const workflowDialogOpen = Boolean(window.__workloopVoiceWorkflowOpen);
  workflowOpens.forEach(fireAction);

  const taskRelated = rest.some((action) => (
    action.type === 'title' || action.type === 'description' || action.type === 'delegate' || action.type === 'publish'
  ));
  if (hasOpen || (!dialogOpen && !workflowDialogOpen && !workflowOpens.length && taskRelated)) {
    fireAction({ type: 'open' });
  }

  const runRest = () => rest.forEach(fireAction);
  if (!rest.length) return;
  if (hasOpen || workflowOpens.length || (!dialogOpen && !workflowDialogOpen)) {
    window.setTimeout(runRest, 450);
  } else {
    runRest();
  }
};
